import { CellStateStyle } from "@maxgraph/core";
import { CustomProperties, Identifier } from "../../models/basic.models";
import { CellKind } from "./drawing.models";

/**
 * Enum para definir as direções do layout.
 */
export enum LayoutDirection {
    LEFT_TO_RIGHT = "LEFT_TO_RIGHT",
    TOP_TO_BOTTOM = "TOP_TO_BOTTOM"
}

/**
 * Enum para definir as formas que o layout pode lidar com tamanhos.
 */
export enum SizingType {
    FIT     = "FIT",
    GROW    = "GROW",
    PERCENT = "PERCENT",
    FIXED   = "FIXED"
}

/**
 * Enum para definir as direções do eixo do layout.
 */
export enum Axis {
    ALONG  = "ALONG",
    ACROSS = "ACROSS"
}

/**
 * Tipo union que define as propriedades de padding horizontal e vertical.
 */
export type PaddingProperty = HorizontalPaddingProperty | VerticalPaddingProperty;

/**
 * Tipo union que define as propriedades de padding horizontal.
 */
export type HorizontalPaddingProperty = "left" | "right";

/**
 * Tipo union que define as propriedades de padding vertical.
 */
export type VerticalPaddingProperty = "top" | "bottom";

/**
 * Tipo union que define as propriedades de alinhamento.
 */
export type AlignmentProperty = "vertical" | "horizontal";

/**
 * Tipo union que define as propriedades de tamanho.
 */
export type SizeProperty = "width" | "height";

/**
 * Tipo union que define as propriedades de tamanho.
 */
export type MaxSizeProperty = "maxWidth" | "maxHeight";

/**
 * Tipo union que define as propriedades de posicionamento.
 */
export type PositionProperty = "x" | "y";

/**
 * Tipo union que define as opções de alinhamento.
 */
export type Alignment = HorizontalAlignment | VerticalAlignment;

/**
 * Interface para definir objetos de padding.
 */
export interface Padding {
    left:   number;
    right:  number;
    top:    number;
    bottom: number;
}

/**
 * Enum que define os tipos de alinhamento horizontal.
 */
export enum HorizontalAlignment {
    LEFT   = "LEFT",
    CENTER = "CENTER",
    RIGHT  = "RIGHT"
}

/**
 * Enum que define os tipos de alinhamento vertical.
 */
export enum VerticalAlignment {
    TOP    = "TOP",
    MIDDLE = "MIDDLE",
    BOTTOM = "BOTTOM"
}

/**
 * Interface que define as propriedades de posicionamento.
 */
export interface Position {
    x: number;
    y: number;
}

/**
 * Interface que define como deve ser feito o alinhamento do layout.
 */
export interface AlignmentConfig {
    horizontal: HorizontalAlignment;
    vertical:   VerticalAlignment;
}

/**
 * Interface para definir um elemento de layout.
 */
export interface LayoutElement {
    id?:          Identifier;
    name:         string;
    value:        string;
    kind:         CellKind;
    parent:       SimpleLayoutElement;
    children:     SimpleLayoutElement[];
    direction:    LayoutDirection;
    alignment:    Partial<AlignmentConfig>;
    source:       Identifier;
    target:       Identifier;
    sizing:       SizingType;
    isFloating:   boolean;
    offset:       Partial<Position>;
    style:        CellStateStyle;
    width:        number;
    height:       number;
    maxWidth:     number;
    maxHeight:    number;
    x:            number;
    y:            number;
    childGap:     number;
    padding:      Partial<Padding>;
    custom?:      CustomLayoutProperties;
}

/**
 * Interface que define as propriedade customizadas dos elementos de layout.
 *
 * Este modelo estende CustomProperties que aceita qualquer valor,
 * pois em alguns momentos é útil poder declarar algo fora do modelo para evitar
 * acoplamento aqui no LayoutManager que não deveria conhecer detalhes específicos.
 */
export interface CustomLayoutProperties extends CustomProperties {
    hasAnimation?:     boolean;
    onRemove?:         Function;
    setCustomStyle?:   Function;
    getCustomTooltip?: Function;
    buildPopover?:     Function;
}

/**
 * Tipo simplificado para um elemento de layout com as propriedades opcionais.
 */
export type SimpleLayoutElement = Partial<LayoutElement>;

/**
 * Define um mapeamento de eixos e propriedades de alinhamento (i.e. vertical, horizontal).
 */
export type AxisAlignmentMap = {
    [key in Axis]: AlignmentProperty;
}

/**
 * Define um mapeamento de eixos e propriedades de padding (i.e. left, right, top, bottom).
 */
export type AxisPaddingMap = {
    [key in Axis]: PaddingProperty[];
}

/**
 * Define um mapeamento de eixos e propriedades de tamanho (i.e. width, height).
 */
export type AxisSizeMap = {
    [key in Axis]: SizeProperty;
}

/**
 * Define um mapeamento de eixos e propriedades de tamanho máximo (i.e. maxWidth, maxHeight).
 */
export type AxisMaxSizeMap = {
    [key in Axis]: MaxSizeProperty;
}

/**
 * Define um mapeamento de eixos e propriedades de posicionamento (i.e. x, y).
 */
export type AxisPositionMap = {
    [key in Axis]: PositionProperty;
}

/**
 * Define um mapeamento de direções de layout e AxisSizeMap.
 */
export type DirectionAxisAlignmentMap = {
    [key in LayoutDirection]: AxisAlignmentMap;
}

/**
 * Define um mapeamento de direções de layout e AxisPaddingMap.
 */
export type DirectionAxisPaddingMap = {
    [key in LayoutDirection]: AxisPaddingMap;
}

/**
 * Define um mapeamento de direções de layout e AxisSizeMap.
 */
export type DirectionAxisSizeMap = {
    [key in LayoutDirection]: AxisSizeMap;
}

/**
 * Define um mapeamento de direções de layout e AxisMaxSizeMap.
 */
export type DirectionAxisMaxSizeMap = {
    [key in LayoutDirection]: AxisMaxSizeMap;
}

/**
 * Define um mapeamento de direções de layout e AxisPositionMap.
 */
export type DirectionAxisPositionMap = {
    [key in LayoutDirection]: AxisPositionMap;
}

/**
 * Mapa do tipo DirectionAxisAlignmentMap, que define quais propriedades de alinhamento
 * se aplicam a quais eixos de acordo com a direção do layout.
 */
export const ALIGNMENT_CONFIG_MAPPING: DirectionAxisAlignmentMap = {
    [LayoutDirection.LEFT_TO_RIGHT]: {
        ALONG:  "horizontal",
        ACROSS: "vertical"
    },
    [LayoutDirection.TOP_TO_BOTTOM]: {
        ALONG:  "vertical",
        ACROSS: "horizontal"
    }
}

/**
 * Mapa do tipo DirectionAxisSizeMap, que define quais propriedades de tamanho
 * se aplicam a quais eixos de acordo com a direção do layout.
 */
export const AXIS_SIZE_MAPPING: DirectionAxisSizeMap = {
    [LayoutDirection.LEFT_TO_RIGHT]: {
        ALONG:  "width",
        ACROSS: "height"
    },
    [LayoutDirection.TOP_TO_BOTTOM]: {
        ALONG:  "height",
        ACROSS: "width"
    }
}

/**
 * Mapa do tipo DirectionAxisSizeMap, que define quais propriedades de tamanho
 * se aplicam a quais eixos de acordo com a direção do layout.
 */
export const AXIS_MAX_SIZE_MAPPING: DirectionAxisMaxSizeMap = {
    [LayoutDirection.LEFT_TO_RIGHT]: {
        ALONG:  "maxWidth",
        ACROSS: "maxHeight"
    },
    [LayoutDirection.TOP_TO_BOTTOM]: {
        ALONG:  "maxHeight",
        ACROSS: "maxWidth"
    }
}

/**
 * Mapa do tipo DirectionAxisPaddingMap, que define quais propriedades de padding
 * se aplicam a quais eixos de acordo com a direção do layout.
 */
export const AXIS_PADDING_MAPPING: DirectionAxisPaddingMap = {
    [LayoutDirection.LEFT_TO_RIGHT]: {
        ALONG:  ["left", "right"],
        ACROSS: ["top", "bottom"]
    },
    [LayoutDirection.TOP_TO_BOTTOM]: {
        ALONG:  ["top", "bottom"],
        ACROSS: ["left", "right"]
    }
}

/**
 * Mapa do tipo DirectionAxisPositionMap, que define quais propriedades de posicionamento
 * se aplicam a quais eixos de acordo com a direção do layout.
 */
export const AXIS_POSITION_MAPPING: DirectionAxisPositionMap = {
    [LayoutDirection.LEFT_TO_RIGHT]: {
        ALONG:  "x",
        ACROSS: "y"
    },
    [LayoutDirection.TOP_TO_BOTTOM]: {
        ALONG:  "y",
        ACROSS: "x"
    }
}

/**
 * Direção padrão do layout.
 */
export const DEFAULT_LAYOUT = LayoutDirection.LEFT_TO_RIGHT;

/**
 * Padding padrão do layout.
 */
export const DEFAULT_PADDING: Padding = {
    left:   0,
    right:  0,
    top:    0,
    bottom: 0
}

/**
 * Offset padrão do layout.
 */
export const DEFAULT_OFFSET: Position = {
    x: 0,
    y: 0
};

/**
 * Alinhamento padrão do layout.
 */
export const DEFAULT_ALIGNMENT: AlignmentConfig = {
    vertical:   VerticalAlignment.TOP,
    horizontal: HorizontalAlignment.LEFT
};
